import {
    Drawer,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    ToggleButton,
    Tooltip,
    Pagination,
    TextField,
    InputAdornment,
    Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import ProductoRow from "./ProductoRow";
import { palette, tono } from "./consolidadoPalette";
import { obtenerEstadoProducto, ESTADOS_PRODUCTO, ORDEN_ESTATUS_PRODUCTO } from "./estadoProducto";
import { ResumenConsolidado, calcularResumen, ToneChip, textoBusqueda } from "./consolidadoUI";

// Consolidado de Producción por PROFORMA. Mismo diseño que
// RetirosConsolidadoDrawer (encabezado, resumen, buscador y filtros).
// El endpoint y las props de siempre no cambian; folioInternoEnvio es
// opcional y solo se usa para el subtítulo.

const ESTATUS_PROFORMA = {
    pendiente: { label: "Pendiente", tone: "neutral" },
    activa: { label: "Activa", tone: "warning" },
    finalizada: { label: "Finalizada", tone: "success" }
};

const getEstatusProforma = (estatus) =>
    ESTATUS_PROFORMA[estatus] || { label: estatus || "—", tone: "neutral" };

export default function ConsolidadoDrawer({
    open,
    onClose,
    envioId,
    proforma,
    folioInternoEnvio
}) {

    const apiUrl =
        process.env.NODE_ENV === "production"
            ? process.env.REACT_APP_API_URL
            : process.env.REACT_APP_API_URL_LOCAL;

    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [filtroEstatus, setFiltroEstatus] = useState([]);
    const [busqueda, setBusqueda] = useState("");
    const [ordenarPorUrgencia, setOrdenarPorUrgencia] = useState(true);
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(25);

    const proformaId = proforma?.proforma_id;

    const cargar = useCallback(async () => {
        if (!proformaId) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(
                `${apiUrl}/empaque/proformas/${proformaId}/envios/${envioId}/consolidado`
            );
            setProductos(data.data || []);
        } catch (err) {
            console.error(err);
            setProductos([]);
            setError(err.response?.data?.message || "No se pudo cargar el consolidado de la proforma.");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, envioId, proformaId]);

    useEffect(() => {
        if (!open || !proformaId) return;
        setFiltroEstatus([]);
        setBusqueda("");
        setPagina(1);
        cargar();
    }, [open, proformaId, cargar]);

    // ------------------------------------------------------------
    // Derivados
    // ------------------------------------------------------------

    // Cada producto trae ya resuelto su _estado (mismo cálculo que usa
    // ProductoRow) para filtrar/ordenar/contar sin duplicar la lógica.
    const productosConEstado = useMemo(
        () => productos.map((p) => ({ ...p, _estado: obtenerEstadoProducto(p), _texto: textoBusqueda(p) })),
        [productos]
    );

    const conteoPorEstatus = useMemo(() => {
        const mapa = {};
        productosConEstado.forEach((p) => {
            mapa[p._estado.status] = (mapa[p._estado.status] || 0) + 1;
        });
        return mapa;
    }, [productosConEstado]);

    const resumen = useMemo(() => calcularResumen(productosConEstado), [productosConEstado]);

    const productosFiltrados = useMemo(() => {
        const q = busqueda.trim().toLowerCase();

        let lista = productosConEstado.filter((p) =>
            (filtroEstatus.length === 0 || filtroEstatus.includes(p._estado.status)) &&
            (!q || p._texto.includes(q))
        );

        if (ordenarPorUrgencia) {
            lista = [...lista].sort(
                (a, b) =>
                    ORDEN_ESTATUS_PRODUCTO.indexOf(a._estado.status) -
                    ORDEN_ESTATUS_PRODUCTO.indexOf(b._estado.status)
            );
        }

        return lista;
    }, [productosConEstado, filtroEstatus, busqueda, ordenarPorUrgencia]);

    // Si el filtro deja menos páginas de las que había, regresamos a la
    // última página válida en vez de mostrar una página vacía.
    const totalPaginas = Math.max(1, Math.ceil(productosFiltrados.length / porPagina));

    useEffect(() => {
        if (pagina > totalPaginas) setPagina(totalPaginas);
    }, [pagina, totalPaginas]);

    const productosPagina = useMemo(() => {
        const inicio = (pagina - 1) * porPagina;
        return productosFiltrados.slice(inicio, inicio + porPagina);
    }, [productosFiltrados, pagina, porPagina]);

    const rangoInicio = productosFiltrados.length === 0 ? 0 : (pagina - 1) * porPagina + 1;
    const rangoFin = Math.min(pagina * porPagina, productosFiltrados.length);

    const resetPagina = () => setPagina(1);

    const handleChangeFiltroEstatus = (event) => {
        const { value } = event.target;
        setFiltroEstatus(typeof value === "string" ? value.split(",") : value);
        resetPagina();
    };

    const estProforma = proforma?.estatus ? getEstatusProforma(proforma.estatus) : null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: "95%",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: palette.surfaceMuted
                }
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>

                {/* HEADER */}
                <Box
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderBottom: `1px solid ${palette.border}`,
                        bgcolor: palette.surface,
                        flexShrink: 0
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <FolderCopyOutlinedIcon color="primary" />
                            Consolidado de Producción
                            {proformaId != null && (
                                <ToneChip label={`Proforma #${proformaId}`} tone="primary" sx={{ fontWeight: 700 }} />
                            )}
                            {estProforma && <ToneChip label={estProforma.label} tone={estProforma.tone} />}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Tooltip title="Vuelve a consultar el consolidado." arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        onClick={cargar}
                                        disabled={loading}
                                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                                    >
                                        Actualizar
                                    </Button>
                                </span>
                            </Tooltip>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        Proforma #{proformaId} • Envío {folioInternoEnvio || `#${envioId}`}
                    </Typography>

                    {/* Resumen */}
                    <ResumenConsolidado
                        aEnviar={resumen.aEnviar}
                        empacado={resumen.empacado}
                        pendiente={resumen.pendiente}
                        pct={resumen.pct}
                    />

                    {/* Filtros */}
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}>
                        <TextField
                            size="small"
                            placeholder="Buscar SKU, título, ML u OP"
                            value={busqueda}
                            onChange={(e) => {
                                setBusqueda(e.target.value);
                                resetPagina();
                            }}
                            sx={{ minWidth: 260 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel id="filtro-estatus-consolidado-label">Filtrar por estatus</InputLabel>
                            <Select
                                labelId="filtro-estatus-consolidado-label"
                                multiple
                                value={filtroEstatus}
                                onChange={handleChangeFiltroEstatus}
                                input={<OutlinedInput label="Filtrar por estatus" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const info = ESTADOS_PRODUCTO.find((e) => e.value === value);
                                            return (
                                                <Chip
                                                    key={value}
                                                    size="small"
                                                    label={info?.label || value}
                                                    sx={{
                                                        bgcolor: tono(info?.tone).bg,
                                                        color: tono(info?.tone).text,
                                                        border: `1px solid ${tono(info?.tone).border}`,
                                                        fontWeight: 600
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {ESTADOS_PRODUCTO.map((info) => (
                                    <MenuItem key={info.value} value={info.value}>
                                        <Checkbox checked={filtroEstatus.indexOf(info.value) > -1} />
                                        <ListItemText
                                            primary={info.label}
                                            secondary={`${conteoPorEstatus[info.value] || 0} producto(s)`}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Tooltip title="Muestra primero lo más urgente (pendiente por surtir/empacar) y al final lo ya empacado">
                            <ToggleButton
                                size="small"
                                value="ordenar"
                                selected={ordenarPorUrgencia}
                                onChange={() => {
                                    setOrdenarPorUrgencia((prev) => !prev);
                                    resetPagina();
                                }}
                                sx={{ textTransform: "none", gap: 0.5 }}
                            >
                                <SortIcon fontSize="small" />
                                Ordenar por urgencia
                            </ToggleButton>
                        </Tooltip>

                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel id="por-pagina-consolidado-label">Por página</InputLabel>
                            <Select
                                labelId="por-pagina-consolidado-label"
                                label="Por página"
                                value={porPagina}
                                onChange={(e) => {
                                    setPorPagina(Number(e.target.value));
                                    resetPagina();
                                }}
                            >
                                <MenuItem value={25}>25 por página</MenuItem>
                                <MenuItem value={50}>50 por página</MenuItem>
                            </Select>
                        </FormControl>

                        <Typography variant="caption" sx={{ color: palette.textSecondary }}>
                            Mostrando {rangoInicio}–{rangoFin} de {productosFiltrados.length}
                            {productosFiltrados.length !== productos.length && ` (filtrado de ${productos.length})`}
                        </Typography>
                    </Box>
                </Box>

                {/* CONTENIDO */}
                <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, p: 2 }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" mt={5}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Box display="flex" justifyContent="center" mt={5}>
                            <Typography variant="body2" color="error">{error}</Typography>
                        </Box>
                    ) : productosFiltrados.length === 0 ? (
                        <Box display="flex" justifyContent="center" mt={5}>
                            <Typography variant="body2" color="text.secondary">
                                {productos.length === 0
                                    ? "Esta proforma no tiene productos en este envío."
                                    : "No hay productos con los filtros seleccionados."}
                            </Typography>
                        </Box>
                    ) : (
                        productosPagina.map((producto) => (
                            <ProductoRow
                                key={producto.producto_id}
                                producto={producto}
                            />
                        ))
                    )}
                </Box>

                {!loading && totalPaginas > 1 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 1.5,
                            borderTop: `1px solid ${palette.border}`,
                            bgcolor: palette.surface,
                            flexShrink: 0
                        }}
                    >
                        <Pagination
                            count={totalPaginas}
                            page={pagina}
                            onChange={(event, value) => setPagina(value)}
                            color="primary"
                            size="small"
                        />
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}
